import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toaster from '@cashew/toaster';

export interface MerchantStoreData {
  id: number;
  image: string;
  merchantId: number;
  status: string;
  storeAddress: string;
  storeName: string;
  storeUrl: string;
  type: string;
}

export const countries = ['UAE', 'KSA', ''] as const;
export type Country = (typeof countries)[number];
export const countriesAsStrings = countries.map<string>((value) => value);
export const isCountry = (value: string): value is Country =>
  countriesAsStrings.includes(value);

export const emirates = [
  'AJMAN',
  'RAS_AL_KHAIMAH',
  'ABU_DHABI',
  'SHARJAH',
  'UMM_AL_QUWAIN',
  'FUJAIRAH',
  'DUBAI',
  '',
] as const;
export type Emirate = (typeof emirates)[number];
export const emiratesAsStrings = emirates.map<string>((value) => value);
export const isEmirate = (value: string): value is Emirate =>
  emiratesAsStrings.includes(value);

export interface MerchantBank {
  id: number;
  key: string;
  value: string;
}

export const merchantBankEndpoint = 'identity/merchant-bank';

export interface MerchantData {
  bankId?: number;
  cyclePeriod?: number;
  id?: number;
  stores: MerchantStoreData[];

  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  companyCountry?: Country;
  companyName?: string;
  country?: string;
  email?: string;
  emirate?: string;
  phoneNumber?: string;
  poBox?: string;
  representativeContractNumber?: string;
  representativeEmail?: string;
  representativeFirstName?: string;
  representativeLastName?: string;
  tradeLicense?: string;
  vatNumber?: string;

  accountHolder?: string;
  accountNumber?: string;
  acquirerBank?: string;
  bank?: string;
  bankName?: string;
  ibanNumber?: string;
  createDate?: string;
}

export interface DefaultSectionProps {
  merchantData: MerchantData | null;
  isLoading: boolean;
  setIsSectionValid: (newValue: boolean) => void;
}

const initialToasterError = Object.freeze({
  isOpen: false,
  title: '',
  message: '',
});

export type ToasterError = { isOpen: boolean; title: string; message: string };

export interface ErrorWithMessage {
  response?: {
    data?: {
      message?: string;
      errorCode?: string;
    };
  };
}

export type SetToasterError = (title: string, error: ErrorWithMessage) => void;

// eslint-disable-next-line no-undef
type UseToasterErrorStateReturn = [() => JSX.Element, SetToasterError];

export const useToasterErrorState = (
  setIsSaving?: (value: boolean) => void
): UseToasterErrorStateReturn => {
  const { t } = useTranslation();
  const [toasterError, _setToasterError] =
    useState<ToasterError>(initialToasterError);
  const toasterErrorOnClick = useCallback(() => {
    _setToasterError(initialToasterError);
    if (setIsSaving) {
      setIsSaving(false);
    }
  }, [_setToasterError, setIsSaving]);
  const ErrorToaster = useCallback(
    () => (
      <Toaster
        title={toasterError.title}
        isVisible={toasterError.isOpen}
        message={toasterError.message}
        type="error"
        onDismiss={toasterErrorOnClick}
      />
    ),
    [toasterError, toasterErrorOnClick]
  );
  const setToasterError = useCallback(
    (title: string, error: ErrorWithMessage) =>
      _setToasterError({
        isOpen: true,
        title,
        message:
          error?.response?.data?.message && error.response.data.errorCode
            ? `${error.response.data.message} (error code: ${error.response.data.errorCode})`
            : t('onboardingForm.error.noMessage'),
      }),
    [_setToasterError, t]
  );

  return [ErrorToaster, setToasterError];
};

// eslint-disable-next-line no-undef
type UseToasterSuccessStateReturn = [() => JSX.Element, (title: string, message: string) => void];

export const useToasterSuccessState = (): UseToasterSuccessStateReturn => {
  const [toasterSuccess, _setToasterSuccess] =
    useState<ToasterError>(initialToasterError);
  const toasterSuccessOnDismiss = useCallback(() => {
    _setToasterSuccess(initialToasterError);
  }, []);
  const SuccessToaster = useCallback(
    () => (
      <Toaster
        title={toasterSuccess.title}
        isVisible={toasterSuccess.isOpen}
        message={toasterSuccess.message}
        type="success"
        onDismiss={toasterSuccessOnDismiss}
      />
    ),
    [toasterSuccess, toasterSuccessOnDismiss]
  );
  const setToasterSuccess = useCallback(
    (title: string, message: string) =>
      _setToasterSuccess({ isOpen: true, title, message }),
    []
  );

  return [SuccessToaster, setToasterSuccess];
};

export const isObject = (
  value: unknown
): value is Record<string, unknown> | null | unknown[] =>
  typeof value === 'object';

export const isStringUnknownRecord = (
  value: unknown
): value is Record<string, unknown> =>
  isObject(value) && value !== null && !Array.isArray(value);

export const merchantEndpoint = (merchantId: string) =>
  `identity/merchant/${merchantId}`;
